import type { ReactNode } from "react";

import { AuthErrorPage } from "@/auth/AuthErrorPage";
import { AppBootstrapProvider } from "@/context/AppBootstrapContext";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useUser } from "@/auth/UserContext";
import { useSyncFeedViewerParty } from "@/features/feed/feedViewerParty";
import { useSyncViewerBookingScope } from "@/hooks/useViewerBookingScope";

/** Holds the splash until the user profile and feature flags have loaded. */
export function AppBootstrapGate({ children }: { children: ReactNode }) {
  const {
    profile,
    isProfileLoading,
    profileError,
    refreshProfile,
    logout,
  } = useUser();
  const { loading: flagsLoading } = useFeatureFlags();
  useSyncViewerBookingScope();
  useSyncFeedViewerParty();

  const profileSettled = !isProfileLoading;
  const ready =
    profileSettled && !flagsLoading && (profile !== null || profileError !== null);

  if (profileError && profileSettled && !flagsLoading) {
    return (
      <AuthErrorPage
        errorCode="profile_load_failed"
        errorDescription={profileError}
        onRetry={() => void refreshProfile()}
        onLogout={logout}
      />
    );
  }

  return <AppBootstrapProvider ready={ready}>{children}</AppBootstrapProvider>;
}
